import { Outlet, useLocation, useParams } from '@tanstack/react-router';
import type { GetShipmentsFilterStatusesItem as ShipmentFilterStatus } from '@uturn/api/v2';
import { Page } from '@uturn/portal/components/page';
import { useFacetSearch } from '@uturn/portal/hooks/queries/meilisearch';
import useShipmentViews, {
	ShipmentView,
} from '@uturn/portal/hooks/shipments/use-shipment-view';
import {
	ShipmentsCancelModal,
	ShipmentsExportModal,
} from '@uturn/portal/modules/shipments/components';
import { NON_CANCELLABLE_BULK_STATUSES } from '@uturn/portal/modules/shipments/components/shipments-cancel/shipments-cancel';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import {
	GROUP_SHIPMENTS_STATUS_TABS,
	type StatusTab,
} from '@uturn/portal/types/shipments';
import type { TFunction } from 'i18next';
import posthog from 'posthog-js';
import { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
	MultiUpdateModal,
	type UpdateFilters,
} from '../components/multi-update/multi-update-dialog';
import { EventsHistorySheet } from '../components/shipments-sheet';
import { ShipmentGroupDetailContext } from '../providers/shipment-group-detail';
import { ShipmentViewProvider } from '../providers/shipment-view';

const getPublishStateAction = (activeStatusTab: StatusTab, t: TFunction) => {
	const publishStateActionByTab: Partial<
		Record<StatusTab, 'publish' | 'unpublish'>
	> = {
		drafted: 'publish',
		published: 'unpublish',
	};
	const action = publishStateActionByTab[activeStatusTab];

	const label =
		action === 'unpublish'
			? t('pages.shippers.actions.unpublish', 'Unpublish')
			: t('pages.shippers.actions.publish', 'Publish');

	return { action, label };
};

export function ShipmentsGroupDetailLayout() {
	const { t } = useTranslation();
	const { shipmentView, getShipmentViewTabs } = useShipmentViews();

	const {
		totalShipments,
		initTabs,
		tabs,
		statuses,
		shipmentsSearchFilters,
		activeStatusTab,
	} = useShipmentsSearchStore();
	const pathname = useLocation({
		select: (location) => location.pathname,
	});
	const { groupName, groupId, setGroupId } = useContext(
		ShipmentGroupDetailContext,
	);

	const params = useParams({
		from: '/_protected/shipments/groups/$groupId',
	});

	// TODO: Improve this without useEffect.
	//       Move this to on tab click and on group click in ShipmentGroupsPage.
	useEffect(() => {
		if (params.groupId) {
			setGroupId(params.groupId.toString());
			initTabs(
				t,
				GROUP_SHIPMENTS_STATUS_TABS,
				pathname,
				params.groupId.toString(),
			);
		}

		return () => {
			setGroupId('');
		};
	}, [pathname, params.groupId]);

	const showShipmentsCancelModal = !pathname.includes('/archived');
	const showShipmentsUpdateModal =
		showShipmentsCancelModal && totalShipments > 0;

	const activeFilterStatuses =
		shipmentsSearchFilters?.['filter.statuses'] ?? statuses;
	const emptyStatusSelection =
		Array.isArray(shipmentsSearchFilters?.['filter.statuses']) &&
		shipmentsSearchFilters['filter.statuses'].length === 0;
	const statusFilter =
		activeFilterStatuses.length > 0
			? [`shipmentStatus IN [${activeFilterStatuses.join(',')}]`]
			: [];
	const facetSearch = useFacetSearch('shipmentStatus', groupId, statusFilter);
	const nonCancellableCount =
		facetSearch.data?.facetHits
			.filter((hit) =>
				NON_CANCELLABLE_BULK_STATUSES.includes(
					hit.value as ShipmentFilterStatus,
				),
			)
			.reduce((sum, hit) => sum + hit.count, 0) ?? 0;
	const noCancellableShipments =
		totalShipments > 0 && totalShipments - nonCancellableCount <= 0;
	const isCancellingDisabled = noCancellableShipments || emptyStatusSelection;

	const [openEvents, setOpenEvents] = useState(false);
	const [openExport, setOpenExport] = useState(false);
	const [openCancel, setOpenCancel] = useState(false);
	const [openMultiUpdate, setOpenMultiUpdate] = useState(false);
	const [preselectPublish, setPreselectPublish] = useState(false);

	const { action: publishStateAction, label: publishStateLabel } =
		getPublishStateAction(activeStatusTab, t);

	return (
		<>
			<Helmet title={groupName} />
			<Page
				title={groupName}
				backButton={{
					label: `Shipment groups`,
					href: '/shipments/groups',
				}}
				secondaryAction={{
					label: t('pages.shippers.actions.events', 'Events'),
					onClick: () => setOpenEvents(true),
				}}
				contextActions={
					shipmentView === ShipmentView.Table
						? undefined
						: [
								{
									label: t('pages.shippers.actions.export', 'Export'),
									onClick: () => setOpenExport(true),
								},
								{
									label: t('pages.shippers.actions.cancel', 'Cancel'),
									onClick: () => setOpenCancel(true),
									renderCondition: showShipmentsCancelModal,
									disabled: isCancellingDisabled,
									disabledTooltip: isCancellingDisabled
										? t(
												'pages.shipments_cancel.dropdown.no-cancellable-disabled',
												'No shipments in the current view can be cancelled.',
											)
										: undefined,
								},
								{
									label: t(
										'pages.shippers.actions.multi-update',
										'Multi-update',
									),
									onClick: () => {
										setPreselectPublish(false);
										setOpenMultiUpdate(true);
										posthog.capture('bulk_update_modal', {
											name: 'action_menu_button_bulk_update',
											group_id: groupId,
										});
									},
									renderCondition: showShipmentsUpdateModal,
									id: `action-menu-item-multi-update`,
								},
								{
									label: publishStateLabel,
									onClick: () => {
										setPreselectPublish(true);
										setOpenMultiUpdate(true);
										posthog.capture('bulk_update_modal', {
											name: 'action_menu_button_bulk_publish',
											group_id: groupId,
										});
									},
									renderCondition:
										showShipmentsUpdateModal &&
										publishStateAction !== undefined,
									id: `action-menu-item-publish-state`,
								},
							]
				}
				tabsStart={tabs}
				tabsEnd={getShipmentViewTabs()}
			>
				<EventsHistorySheet
					openSheet={openEvents}
					setOpenSheet={setOpenEvents}
				/>
				<ShipmentsExportModal
					exportFilters={shipmentsSearchFilters}
					isOpenModal={openExport}
					setOpenModal={setOpenExport}
					totalShipments={totalShipments}
				/>
				{showShipmentsCancelModal && (
					<ShipmentsCancelModal
						exportFilters={shipmentsSearchFilters}
						statuses={statuses}
						isOpenModal={openCancel}
						setOpenModal={setOpenCancel}
						nonCancellableCount={nonCancellableCount}
					/>
				)}
				{showShipmentsUpdateModal && (
					<MultiUpdateModal
						openModal={openMultiUpdate}
						setOpenModal={setOpenMultiUpdate}
						exportFilters={shipmentsSearchFilters as UpdateFilters}
						preselectPublish={preselectPublish}
					/>
				)}

				<ShipmentViewProvider view={shipmentView}>
					<Outlet />
				</ShipmentViewProvider>
			</Page>
		</>
	);
}
